<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  loadContent: { type: Function, required: true },
  saveContent: { type: Function, required: true },
});

const emit = defineEmits(['saved']);
const { t } = useI18n();
const dialogRef = ref(null);
const documentId = ref(null);
const documentName = ref('');
const content = ref('');
const isLoading = ref(false);
const isSaving = ref(false);
const canSave = computed(
  () => !isLoading.value && !isSaving.value && content.value.trim().length > 0
);

const open = async document => {
  documentId.value = document.id;
  documentName.value = document.name;
  content.value = '';
  isLoading.value = true;
  dialogRef.value.open();
  try {
    content.value = await props.loadContent(document.id);
  } catch (error) {
    dialogRef.value.close();
    useAlert(
      error?.response?.data?.error || t('ARROWS_AI.DOCUMENTS.CONTENT_ERROR')
    );
  } finally {
    isLoading.value = false;
  }
};

const save = async () => {
  if (!canSave.value) return;

  isSaving.value = true;
  try {
    const updated = await props.saveContent(documentId.value, content.value);
    dialogRef.value.close();
    emit('saved', updated);
    useAlert(
      t('ARROWS_AI.DOCUMENTS.EDIT_SUCCESS', { name: documentName.value })
    );
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('ARROWS_AI.DOCUMENTS.EDIT_ERROR')
    );
  } finally {
    isSaving.value = false;
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    width="2xl"
    overflow-y-auto
    :title="t('ARROWS_AI.DOCUMENTS.EDIT_TITLE', { name: documentName })"
    :description="t('ARROWS_AI.DOCUMENTS.EDIT_DESCRIPTION')"
    :confirm-button-label="t('ARROWS_AI.SAVE')"
    :cancel-button-label="t('ARROWS_AI.CANCEL')"
    :is-loading="isSaving"
    :disable-confirm-button="!canSave"
    @confirm="save"
  >
    <textarea
      v-model="content"
      :disabled="isLoading || isSaving"
      class="min-h-64 w-full rounded-lg border border-n-weak bg-n-solid-1 p-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
    />
  </Dialog>
</template>
